import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { getCustomers, assignCustomer } from '@/utils/api';
import { toast } from 'sonner';

const AssignCustomerDialog = ({ task, open, onClose, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCustomers();
      // Pre-select current customer if exists
      if (task?.assigned_customer_id) {
        setSelectedCustomer(task.assigned_customer_id);
      }
    }
  }, [open, task]);

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await assignCustomer(task.id, selectedCustomer);
      toast.success('Customer assigned successfully! They can now view this CAD file.');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="assign-customer-dialog">
        <DialogHeader>
          <DialogTitle>Assign Customer Access</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-500">CAD File</p>
            <p className="font-semibold text-slate-900">{task?.client_name}</p>
            <p className="text-sm text-slate-500 font-mono mt-1">{task?.internal_cad_no}</p>
            {task?.assigned_customer_name && (
              <p className="text-sm text-green-600 mt-2">
                Currently assigned to: <strong>{task.assigned_customer_name}</strong>
              </p>
            )}
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              When you assign a customer to this CAD file, they will be able to:
            </p>
            <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
              <li>View this design in their dashboard</li>
              <li>Chat with your team about this design</li>
              <li>Request revisions</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="customer">Select Customer</Label>
              <select
                id="customer"
                data-testid="customer-select"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                required
                className="mt-2 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              >
                <option value="">Choose a customer...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} {customer.company_name ? `(${customer.company_name})` : ''} - {customer.email}
                  </option>
                ))}
              </select>
              {customers.length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  No customers found. Create a customer account first from the User Management page.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !selectedCustomer} 
                data-testid="confirm-assign-customer-button"
              >
                {loading ? 'Assigning...' : 'Assign Customer'}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignCustomerDialog;
